import React, { useState, useEffect } from 'react';
import {
  Button,
  Space,
  Typography,
  Row,
  Col,
  Radio,
  Checkbox,
  Progress,
  Divider,
  Tag,
  Spin,
  Image
} from 'antd';
import { sanitizeHtml } from '../utils/sanitize';
import {
  ArrowRightOutlined,
  ArrowLeftOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  TrophyOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import questionnaireService from '../services/questionnaireService';

const { Title, Text } = Typography;

const QuizPlayer = () => {
  const { questionnaireId } = useParams();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(0); // 0 = intro, 1 to introPages.length = intro pages, then questions
  const [userAnswers, setUserAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [results, setResults] = useState(null);
  const [startTime] = useState(Date.now());
  const [endTime, setEndTime] = useState(null);

  // Style definitions - moved to top to avoid reference errors
  const backgroundColor = questionnaire?.backgroundColor || 'linear-gradient(135deg, #000 0%, #1a1a1a 100%)';
  const textColor = questionnaire?.textColor || '#ffffff';
  
  const pageStyle = {
    background: backgroundColor,
    minHeight: '100vh',
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: textColor
  };

  const containerStyle = {
    backgroundColor: 'transparent',
    padding: '40px',
    color: textColor,
    maxWidth: '100%'
  };

  const textStyle = {
    color: textColor
  };

  // Helper functions for step calculations
  const getIntroductionPages = () => {
    const pages = questionnaire?.introductionPages || [];
    console.log('getIntroductionPages called, found:', pages);
    return pages;
  };
  const getTotalIntroSteps = () => 1 + getIntroductionPages().length; // Main intro + intro pages
  const isMainIntro = () => currentStep === 0;
  const isIntroductionPage = () => {
    const result = currentStep > 0 && currentStep < getTotalIntroSteps();
    console.log(`isIntroductionPage: currentStep=${currentStep}, totalIntroSteps=${getTotalIntroSteps()}, result=${result}`);
    return result;
  };
  const isQuestionStep = () => currentStep >= getTotalIntroSteps();
  const getCurrentIntroPageIndex = () => currentStep - 1; // currentStep 1 = introPage 0
  const getCurrentQuestionIndex = () => currentStep - getTotalIntroSteps(); // Adjust for intro steps

  useEffect(() => {
    const fetchQuestionnaire = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Use the questionnaire service to fetch for taking
        const response = await questionnaireService.getForTaking(questionnaireId);
        
        if (response.success) {
          setQuestionnaire(response.data);
          console.log('Questionnaire loaded:', response.data);
          console.log('Introduction pages:', response.data.introductionPages);
        } else {
          setError(response.message || 'Failed to load questionnaire');
        }
      } catch (error) {
        console.error('Error fetching questionnaire:', error);
        setError('Failed to load questionnaire. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (questionnaireId) {
      fetchQuestionnaire();
    }
  }, [questionnaireId]);

  // Extract questions array for easier access
  const questions = questionnaire?.questions || [];

  // Mock questionnaire for testing
  const getMockQuestionnaire = () => ({
    id: questionnaireId,
    title: "Sample Quiz Assessment",
    context: `<p><strong>Welcome to the Sample Quiz!</strong></p>
<p>This is a demonstration quiz to test the quiz player functionality. Please read each question carefully and select your answer(s).</p>
<p><strong>Instructions:</strong></p>
<ul>
<li>Navigate through questions using the Previous/Next buttons</li>
<li>Some questions may have multiple correct answers</li>
<li>Your answers will be evaluated at the end</li>
</ul>`,
    questions: [
      {
        id: 1,
        text: "What is the capital of France?",
        type: "single-choice",
        image: "/logo.png", // Sample image for the question
        answers: [
          { id: 1, text: "London", isCorrect: false, explanation: "" },
          { id: 2, text: "Paris", isCorrect: true, explanation: "Paris is the capital and most populous city of France." },
          { id: 3, text: "Berlin", isCorrect: false, explanation: "" },
          { id: 4, text: "Madrid", isCorrect: false, explanation: "" }
        ]
      },
      {
        id: 2,
        text: "Which of the following are programming languages? (Select all that apply)",
        type: "multiple-choice",
        image: "/logo.png", // Sample image for the question
        answers: [
          { id: 1, text: "JavaScript", isCorrect: true, explanation: "JavaScript is a popular programming language.", image: "/logo.png" },
          { id: 2, text: "HTML", isCorrect: false, explanation: "" },
          { id: 3, text: "Python", isCorrect: true, explanation: "Python is a versatile programming language.", image: "/logo.png" },
          { id: 4, text: "CSS", isCorrect: false, explanation: "" },
          { id: 5, text: "Java", isCorrect: true, explanation: "Java is an object-oriented programming language." }
        ]
      },
      {
        id: 3,
        text: "What is 2 + 2?",
        type: "single-choice",
        answers: [
          { id: 1, text: "3", isCorrect: false, explanation: "", image: "/logo.png" },
          { id: 2, text: "4", isCorrect: true, explanation: "2 + 2 equals 4 in basic arithmetic.", image: "/logo.png" },
          { id: 3, text: "5", isCorrect: false, explanation: "" },
          { id: 4, text: "6", isCorrect: false, explanation: "" }
        ]
      },
      {
        id: 4,
        text: "Look at this image and identify what you see:",
        type: "single-choice",
        image: "/logo.png", // Main question image
        answers: [
          { id: 1, text: "A company logo", isCorrect: true, explanation: "This is the company logo displayed as an example.", image: "/logo.png" },
          { id: 2, text: "A random picture", isCorrect: false, explanation: "" },
          { id: 3, text: "A document", isCorrect: false, explanation: "" },
          { id: 4, text: "A screenshot", isCorrect: false, explanation: "" }
        ]
      }
    ]
  });

  if (loading) {
    return (
      <div style={{ 
        background: '#000', 
        minHeight: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        color: '#fff'
      }}>
        <Spin size="large" style={{ color: '#fff' }} />
      </div>
    );
  }

  if (!questionnaire) {
    return (
      <div style={{
        background: '#000',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: '#fff'
      }}>
        <div style={containerStyle}>
          <Title level={3} style={textStyle}>Quiz not found</Title>
        </div>
      </div>
    );
  }
  
  const handleStartQuiz = () => {
    // If we have introduction pages, go to first intro page, otherwise start questions
    const introPages = getIntroductionPages();
    console.log('handleStartQuiz: introPages found:', introPages.length);
    if (introPages.length > 0) {
      console.log('Going to first introduction page (step 1)');
      setCurrentStep(1); // First introduction page
    } else {
      console.log('No intro pages, going directly to questions (step', getTotalIntroSteps(), ')');
      setCurrentStep(getTotalIntroSteps()); // Start questions directly
    }
  };

  const handleAnswerChange = (questionId, selectedAnswers) => {
    setUserAnswers(prev => ({
      ...prev,
      [questionId]: selectedAnswers
    }));
  };

  const handleNextQuestion = async () => {
    if (currentStep < questions.length) {
      setCurrentStep(currentStep + 1);
    } else {
      // Finish quiz - submit to API
      try {
        setLoading(true);
        setEndTime(Date.now());
        
        // Submit answers to the backend
        const response = await questionnaireService.submit(questionnaireId, userAnswers);
        
        if (response.success) {
          setResults(response.data);
          setShowResults(true);
        } else {
          console.error('Failed to submit quiz:', response.message);
          // Fallback to local calculation if API fails
          setResults(calculateLocalResults());
          setShowResults(true);
        }
      } catch (error) {
        console.error('Error submitting quiz:', error);
        // Fallback to local calculation
        setResults(calculateLocalResults());
        setShowResults(true);
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePreviousQuestion = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const calculateLocalResults = () => {
    let correctCount = 0;
    let totalQuestions = questions.length;

    const detailedResults = questions.map(question => {
      const userAnswer = userAnswers[question.id];
      const correctAnswers = question.answers?.filter(a => a.isCorrect) || [];
      
      let isCorrect = false;
      
      if (question.type === 'single') {
        isCorrect = correctAnswers.some(answer => answer.id === userAnswer);
      } else if (question.type === 'multiple') {
        const userAnswerArray = Array.isArray(userAnswer) ? userAnswer : [userAnswer];
        const correctIds = correctAnswers.map(a => a.id);
        isCorrect = correctIds.length === userAnswerArray.length &&
          correctIds.every(id => userAnswerArray.includes(id));
      }
      
      if (isCorrect) correctCount++;
      
      return {
        questionId: question.id,
        questionTitle: question.title,
        userAnswer,
        correctAnswers: correctAnswers.map(a => ({ id: a.id, text: a.text })),
        isCorrect
      };
    });

    return {
      score: totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0,
      passed: totalQuestions > 0 ? (correctCount / totalQuestions) >= 0.7 : false, // 70% passing
      passingScore: 70,
      totalQuestions,
      correctAnswers: correctCount,
      results: detailedResults
    };
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return '#52c41a'; // Green
    if (percentage >= 60) return '#faad14'; // Orange
    return '#ff4d4f'; // Red
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        background: '#000',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Spin size="large" tip="Loading quiz..." />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{
        background: '#000',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: '#fff'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Title level={3} style={{ color: '#ff4d4f' }}>Error Loading Quiz</Title>
          <Text style={{ color: '#fff' }}>{error}</Text>
          <div style={{ marginTop: '16px' }}>
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // No questionnaire data
  if (!questionnaire) {
    return (
      <div style={{
        background: '#000',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: '#fff'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Title level={3} style={{ color: '#fff' }}>Quiz Not Found</Title>
          <Text style={{ color: '#ccc' }}>The requested quiz could not be found.</Text>
        </div>
      </div>
    );
  }

  // Introduction Step
  if (currentStep === 0) {
    return (
      <div style={pageStyle}>
        <div style={{ maxWidth: 1000, width: '100%' }}>
          <div style={containerStyle}>
            {/* Responsive Layout */}
            <Row gutter={[32, 32]} align="middle">
              {/* Content Column - Left on desktop, full width on mobile */}
              <Col xs={24} md={14}>
                <div style={{ textAlign: 'left' }}>
                  <Title level={2} style={{ color: textColor, marginBottom: '24px', fontSize: '2.5rem' }}>
                    Hey there! 👋
                  </Title>
                  
                  <Title level={3} style={{ color: textColor, marginBottom: '24px', fontWeight: 'normal' }}>
                    {questionnaire?.title || 'Welcome to another sizeable Tasty Tuesday'}
                  </Title>
                  
                  {/* Context/Introduction */}
                  {questionnaire?.context ? (
                    <div
                      className="rich-text-content"
                      style={{ 
                        marginBottom: '32px', 
                        fontSize: '16px',
                        lineHeight: '1.6',
                        color: textColor
                      }}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(questionnaire.context) }} 
                    />
                  ) : (
                    <div style={{ marginBottom: '32px', fontSize: '16px', lineHeight: '1.6' }}>
                      <Text style={{ color: textColor }}>
                        Here are 5 questions to see how much you remember from our healthy habits regarding food and cooking, 
                        human anatomy and many things yummy. Let's see how I do...
                      </Text>
                    </div>
                  )}

                  <div style={{ marginBottom: '32px' }}>
                    <Text style={{ fontSize: '16px', color: textColor, opacity: 0.8 }}>
                      📝 {questions.length} questions to test your knowledge
                    </Text>
                  </div>

                  <Button
                    type="primary"
                    size="large"
                    icon={<PlayCircleOutlined />}
                    onClick={handleStartQuiz}
                    style={{ 
                      fontSize: '16px', 
                      height: '48px', 
                      padding: '0 32px',
                      borderRadius: '8px',
                      backgroundColor: textColor === '#ffffff' ? '#fff' : textColor,
                      borderColor: textColor === '#ffffff' ? '#fff' : textColor,
                      color: backgroundColor.includes('52c41a') ? '#52c41a' : '#000',
                      fontWeight: '600'
                    }}
                  >
                    Let's see how I do...
                  </Button>
                </div>
              </Col>

              {/* Image Column - Right on desktop, top on mobile */}
              <Col xs={24} md={10}>
                <div style={{ textAlign: 'center' }}>
                  <Image
                    src={questionnaire?.image || "/Screenshot 2025-10-03 at 23.31.05.png"}
                    alt="Quiz Image"
                    width="100%"
                    height={300}
                    style={{ 
                      objectFit: 'cover',
                      borderRadius: '16px',
                      maxWidth: '400px'
                    }}
                    preview={false}
                  />
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    );
  }

  // Introduction Pages Steps
  if (isIntroductionPage()) {
    const introPage = getIntroductionPages()[getCurrentIntroPageIndex()];
    const isLastIntroPage = currentStep === getTotalIntroSteps() - 1;
    
    return (
      <div style={pageStyle}>
        <div style={{ maxWidth: 1000, width: '100%' }}>
          <div style={containerStyle}>
            {/* Responsive Layout - Text left, Image right (desktop) / Image top, Text bottom (mobile) */}
            <Row gutter={[32, 32]} align="middle">
              {/* Content Column - Left on desktop, bottom on mobile */}
              <Col xs={24} md={introPage?.image ? 12 : 24} order={{ xs: 2, md: 1 }}>
                <div style={{ textAlign: 'left' }}>
                  {/* Page Content */}
                  {introPage?.text && (
                    <div
                      className="rich-text-content"
                      style={{ 
                        marginBottom: '32px', 
                        fontSize: '16px',
                        lineHeight: '1.6',
                        color: textColor
                      }}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(introPage.text) }}
                    />
                  )}

                  <div style={{ marginBottom: '32px' }}>
                    <Text style={{ fontSize: '16px', color: textColor, opacity: 0.8 }}>
                      📄 Page {getCurrentIntroPageIndex() + 1} of {getIntroductionPages().length}
                    </Text>
                  </div>

                  {/* Single centered button */}
                  <div style={{ textAlign: 'center' }}>
                    <Button
                      type="primary"
                      size="large"
                      icon={isLastIntroPage ? <PlayCircleOutlined /> : <ArrowRightOutlined />}
                      onClick={() => setCurrentStep(currentStep + 1)}
                      style={{ 
                        fontSize: '16px', 
                        height: '48px', 
                        padding: '0 32px',
                        borderRadius: '8px',
                        backgroundColor: textColor === '#ffffff' ? '#fff' : textColor,
                        borderColor: textColor === '#ffffff' ? '#fff' : textColor,
                        color: backgroundColor.includes('52c41a') ? '#52c41a' : '#000',
                        fontWeight: '600'
                      }}
                    >
                      {introPage?.buttonText || (isLastIntroPage ? "Let's Start Quiz!" : 'Next')}
                    </Button>
                  </div>
                </div>
              </Col>

              {/* Image Column - Right on desktop, top on mobile (only if image exists) */}
              {introPage?.image && (
                <Col xs={24} md={12} order={{ xs: 1, md: 2 }}>
                  <div style={{ textAlign: 'center', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Image
                      src={introPage.image}
                      alt="Introduction"
                      width="100%"
                      height={400}
                      style={{ 
                        objectFit: 'cover',
                        borderRadius: '16px',
                        maxWidth: '100%',
                        maxHeight: '400px'
                      }}
                      preview={false}
                    />
                  </div>
                </Col>
              )}
            </Row>
          </div>
        </div>
      </div>
    );
  }

  // Results Step with inline answers
  if (showResults) {
    // Use results from state (from API) or calculate locally if not available
    const displayResults = results || calculateLocalResults();
    
    return (
      <div style={{ ...pageStyle, alignItems: 'flex-start', paddingTop: '40px' }}>
        <div style={{ width: '100%' }}>
          {/* Results Summary */}
          <div style={{ ...containerStyle, textAlign: 'center', marginBottom: '24px' }}>
            <TrophyOutlined 
              style={{ 
                fontSize: '48px', 
                color: '#FFD700', 
                marginBottom: '16px' 
              }} 
            />
            <Title level={2} style={{ color: textColor, fontSize: '2.5rem' }}>Congratulations!</Title>
            <Title level={3} style={{ color: textColor, fontWeight: 'normal', marginBottom: '24px' }}>
              You've completed the quiz
            </Title>
            
            <Row gutter={32} justify="center" style={{ marginBottom: '24px' }}>
              <Col>
                <div>
                  <Title level={1} style={{ 
                    color: getScoreColor(displayResults.score), 
                    margin: 0,
                    fontSize: '36px'
                  }}>
                    {displayResults.score}%
                  </Title>
                  <Text style={{ fontSize: '16px', color: textColor, opacity: 0.8 }}>
                    {displayResults.correctAnswers} out of {displayResults.totalQuestions} correct
                  </Text>
                  {displayResults.passed !== undefined && (
                    <div style={{ marginTop: '8px' }}>
                      <Tag color={displayResults.passed ? 'green' : 'red'}>
                        {displayResults.passed ? 'PASSED' : 'FAILED'} (Required: {displayResults.passingScore}%)
                      </Tag>
                    </div>
                  )}
                </div>
              </Col>
              <Col>
                <div>
                  <ClockCircleOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                  <br />
                  <Text strong style={{ fontSize: '16px', color: textColor }}>
                    {formatTime(endTime ? Math.round((endTime - startTime) / 1000) : 0)}
                  </Text>
                </div>
              </Col>
            </Row>

            <Button
              type={showAnswers ? "default" : "primary"}
              onClick={() => setShowAnswers(!showAnswers)}
              style={{ 
                marginRight: '12px',
                backgroundColor: showAnswers ? 'transparent' : (textColor === '#ffffff' ? '#fff' : textColor),
                borderColor: textColor,
                color: showAnswers ? textColor : (backgroundColor.includes('52c41a') ? '#52c41a' : '#000'),
                fontWeight: '600'
              }}
            >
              {showAnswers ? 'Hide Answers' : 'View Answers'}
            </Button>

            <Button
              type="primary"
              onClick={() => {
                setCurrentStep(0);
                setUserAnswers({});
                setShowResults(false);
                setShowAnswers(false);
                setEndTime(null);
              }}
              style={{
                backgroundColor: textColor === '#ffffff' ? '#fff' : textColor,
                borderColor: textColor === '#ffffff' ? '#fff' : textColor,
                color: backgroundColor.includes('52c41a') ? '#52c41a' : '#000',
                fontWeight: '600'
              }}
            >
              Retake Quiz
            </Button>
          </div>

          {/* Inline Answers Display */}
          {showAnswers && (
            <div>
              {questions.map((question, index) => {
                const userAnswer = userAnswers[question.id];
                const correctAnswers = question.answers?.filter(a => a.isCorrect) || [];
                const isCorrect = (() => {
                  if (!userAnswer) return false;
                  const correctIds = correctAnswers.map(a => a.id);
                  if (question.type === 'single') {
                    return correctIds.includes(userAnswer);
                  } else {
                    const userAnswerArray = Array.isArray(userAnswer) ? userAnswer : [userAnswer];
                    return correctIds.length === userAnswerArray.length &&
                      correctIds.every(id => userAnswerArray.includes(id));
                  }
                })();

                return (
                  <div 
                    key={question.id} 
                    style={{ 
                      padding: '20px',
                      marginBottom: '16px',
                      borderLeft: `4px solid ${isCorrect ? '#52c41a' : '#ff4d4f'}`
                    }}
                  >
                    <div style={{ marginBottom: '16px' }}>
                      <Space>
                        <Text strong style={{ color: textColor }}>Question {index + 1}</Text>
                        {isCorrect ? (
                          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                        ) : (
                          <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '18px' }} />
                        )}
                      </Space>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      {question.image && (
                        <Image
                          src={question.image}
                          alt="Question"
                          width={60}
                          height={45}
                          style={{ objectFit: 'cover', borderRadius: '4px' }}
                          preview={true}
                        />
                      )}
                      <Title level={5} style={{ color: textColor, marginBottom: '0' }}>
                        {question.text}
                      </Title>
                    </div>
                    
                    <Row gutter={16}>
                      <Col span={12}>
                        <Text strong style={{ color: '#52c41a' }}>Correct Answer(s):</Text>
                        <div style={{ marginTop: '8px' }}>
                          {correctAnswers.map(answer => (
                            <div key={answer.id} style={{ marginBottom: '8px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Tag color="green">{answer.text}</Tag>
                                {answer.image && (
                                  <Image
                                    src={answer.image}
                                    alt="Correct Answer"
                                    width={30}
                                    height={30}
                                    style={{ objectFit: 'cover', borderRadius: '4px' }}
                                    preview={true}
                                  />
                                )}
                              </div>
                              {answer.explanation && (
                                <div style={{ 
                                  marginTop: '4px', 
                                  padding: '8px', 
                                  backgroundColor: 'rgba(29, 61, 29, 0.3)', 
                                  borderRadius: '4px',
                                  fontSize: '14px',
                                  border: '1px solid rgba(82, 196, 26, 0.3)'
                                }}>
                                  <Text style={{ color: '#7dd87d' }}>
                                    {answer.explanation}
                                  </Text>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </Col>

                      <Col span={12}>
                        <Text strong style={textStyle}>Your Answer:</Text>
                        <div style={{ marginTop: '8px' }}>
                          {userAnswer ? (
                            Array.isArray(userAnswer) ? (
                              <>
                                {userAnswer.map(answerId => {
                                  const answer = question.answers?.find(a => a.id === answerId);
                                  const isAnswerCorrect = correctAnswers.some(ca => ca.id === answerId);
                                  return answer ? (
                                    <div key={answerId} style={{ marginBottom: '8px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {isAnswerCorrect ? (
                                          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
                                        ) : (
                                          <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />
                                        )}
                                        <Tag color={isAnswerCorrect ? 'green' : 'red'}>
                                          {answer.text}
                                        </Tag>
                                        {answer.image && (
                                          <Image
                                            src={answer.image}
                                            alt="Your Answer"
                                            width={30}
                                            height={30}
                                            style={{ objectFit: 'cover', borderRadius: '4px' }}
                                            preview={true}
                                          />
                                        )}
                                      </div>
                                      {!isAnswerCorrect && (
                                        <Text style={{ color: '#ff4d4f', fontSize: '12px', marginLeft: '24px' }}>
                                          Your answer - Incorrect
                                        </Text>
                                      )}
                                    </div>
                                  ) : null;
                                })}
                              </>
                            ) : (
                              (() => {
                                const answer = question.answers?.find(a => a.id === userAnswer);
                                const isAnswerCorrect = correctAnswers.some(ca => ca.id === userAnswer);
                                return answer ? (
                                  <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      {isAnswerCorrect ? (
                                        <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
                                      ) : (
                                        <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />
                                      )}
                                      <Tag color={isAnswerCorrect ? 'green' : 'red'}>
                                        {answer.text}
                                      </Tag>
                                      {answer.image && (
                                        <Image
                                          src={answer.image}
                                          alt="Your Answer"
                                          width={30}
                                          height={30}
                                          style={{ objectFit: 'cover', borderRadius: '4px' }}
                                          preview={true}
                                        />
                                      )}
                                    </div>
                                    {!isAnswerCorrect && (
                                      <Text style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px', display: 'block', marginLeft: '24px' }}>
                                        Your answer - Incorrect
                                      </Text>
                                    )}
                                  </div>
                                ) : null;
                              })()
                            )
                          ) : (
                            <Tag color="default">No answer</Tag>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Question Steps
  if (isQuestionStep()) {
    const currentQuestion = questions[getCurrentQuestionIndex()];
    
    if (!currentQuestion) {
      return (
        <div style={pageStyle}>
          <div style={containerStyle}>
            <Text style={{ color: textColor }}>Question not found</Text>
          </div>
        </div>
      );
    }

    const questionNumber = getCurrentQuestionIndex() + 1;
    const userAnswer = userAnswers[currentQuestion.id];

    return (
      <div style={pageStyle}>
        <div style={{ maxWidth: 1200, width: '100%' }}>
          <div style={containerStyle}>
            {/* Main Content Layout - Question left, Image right (like PDF) */}
            <Row gutter={[40, 32]} align="top">
              {/* Question Content Column - Left side */}
              <Col xs={24} md={currentQuestion?.image ? 14 : 24} order={{ xs: 2, md: 1 }}>
                <div style={{ textAlign: 'left' }}>
                  {/* Question Header */}
                  <div style={{ marginBottom: '32px' }}>
                    <Text 
                      style={{ 
                        fontSize: '18px', 
                        color: textColor, 
                        opacity: 0.9, 
                        fontWeight: '600',
                        display: 'block',
                        marginBottom: '16px'
                      }}
                    >
                      QUESTION {questionNumber} - {currentQuestion.text}
                    </Text>
                    
                    {currentQuestion.description && (
                      <Text 
                        style={{ 
                          fontSize: '14px', 
                          color: textColor, 
                          opacity: 0.7,
                          fontStyle: 'italic',
                          display: 'block'
                        }}
                      >
                        {currentQuestion.description}
                      </Text>
                    )}
                  </div>

                  {/* Answer Options */}
                  <div style={{ marginBottom: '40px', paddingBottom: currentQuestion?.image ? '1px' : '40px' }}>
                    {currentQuestion.type === 'single' ? (
                      <Radio.Group 
                        value={userAnswer} 
                        onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                        style={{ width: '100%' }}
                      >
                        <Space direction="vertical" style={{ width: '100%' }} size="large">
                          {currentQuestion.answers?.map((answer, answerIndex) => (
                            <div 
                              key={answer.id} 
                              style={{ 
                                backgroundColor: userAnswer === answer.id ? `${textColor}20` : 'transparent',
                                border: `2px solid ${userAnswer === answer.id ? textColor : `${textColor}40`}`,
                                borderRadius: '12px',
                                padding: '16px 20px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                minHeight: '60px'
                              }}
                              onClick={() => handleAnswerChange(currentQuestion.id, answer.id)}
                            >
                              {/* Option Letter */}
                              <div style={{
                                backgroundColor: userAnswer === answer.id ? textColor : 'transparent',
                                color: userAnswer === answer.id ? backgroundColor.includes('52c41a') ? '#52c41a' : '#000' : textColor,
                                border: `2px solid ${textColor}`,
                                borderRadius: '50%',
                                width: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 'bold',
                                fontSize: '14px',
                                flexShrink: 0
                              }}>
                                {String.fromCharCode(65 + answerIndex)}
                              </div>
                              
                              {/* Answer Text */}
                              <Radio value={answer.id} style={{ display: 'none' }} />
                              <Text style={{ 
                                fontSize: '16px', 
                                color: textColor, 
                                fontWeight: '500',
                                flex: 1
                              }}>
                                {answer.text}
                              </Text>
                              
                              {/* Answer Image */}
                              {answer.image && (
                                <Image 
                                  src={answer.image} 
                                  alt="Answer"
                                  width={50}
                                  height={50}
                                  style={{ 
                                    objectFit: 'cover', 
                                    borderRadius: '8px',
                                    flexShrink: 0
                                  }}
                                  preview={true}
                                />
                              )}
                            </div>
                          ))}
                        </Space>
                      </Radio.Group>
                    ) : (
                      <Checkbox.Group 
                        value={userAnswer || []} 
                        onChange={(values) => handleAnswerChange(currentQuestion.id, values)}
                        style={{ width: '100%' }}
                      >
                        <Space direction="vertical" style={{ width: '100%' }} size="large">
                          {currentQuestion.answers?.map((answer, answerIndex) => (
                            <div 
                              key={answer.id} 
                              style={{ 
                                backgroundColor: (userAnswer || []).includes(answer.id) ? `${textColor}20` : 'transparent',
                                border: `2px solid ${(userAnswer || []).includes(answer.id) ? textColor : `${textColor}40`}`,
                                borderRadius: '12px',
                                padding: '16px 20px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                minHeight: '60px'
                              }}
                            >
                              {/* Option Letter */}
                              <div style={{
                                backgroundColor: (userAnswer || []).includes(answer.id) ? textColor : 'transparent',
                                color: (userAnswer || []).includes(answer.id) ? backgroundColor.includes('52c41a') ? '#52c41a' : '#000' : textColor,
                                border: `2px solid ${textColor}`,
                                borderRadius: '4px',
                                width: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 'bold',
                                fontSize: '14px',
                                flexShrink: 0
                              }}>
                                {String.fromCharCode(65 + answerIndex)}
                              </div>
                              
                              {/* Answer Checkbox */}
                              <Checkbox value={answer.id} style={{ display: 'none' }} />
                              <Text style={{ 
                                fontSize: '16px', 
                                color: textColor, 
                                fontWeight: '500',
                                flex: 1
                              }}>
                                {answer.text}
                              </Text>
                              
                              {/* Answer Image */}
                              {answer.image && (
                                <Image 
                                  src={answer.image} 
                                  alt="Answer"
                                  width={50}
                                  height={50}
                                  style={{ 
                                    objectFit: 'cover', 
                                    borderRadius: '8px',
                                    flexShrink: 0
                                  }}
                                  preview={true}
                                />
                              )}
                            </div>
                          ))}
                        </Space>
                      </Checkbox.Group>
                    )}
                  </div>

                  {/* Progress and Navigation */}
                  <div style={{ marginBottom: '32px' }}>
                    <Text style={{ fontSize: '16px', color: textColor, opacity: 0.8 }}>
                      📄 Question {questionNumber} of {questions.length} • {Math.round((questionNumber / questions.length) * 100)}% Complete
                    </Text>
                  </div>

                  {/* Single centered navigation button */}
                  <div style={{ textAlign: 'center' }}>
                    <Button
                      type="primary"
                      size="large"
                      icon={questionNumber === questions.length ? <CheckCircleOutlined /> : <ArrowRightOutlined />}
                      onClick={handleNextQuestion}
                      disabled={!userAnswer || (Array.isArray(userAnswer) && userAnswer.length === 0)}
                      style={{ 
                        fontSize: '16px', 
                        height: '48px', 
                        padding: '0 32px',
                        borderRadius: '8px',
                        backgroundColor: textColor === '#ffffff' ? '#fff' : textColor,
                        borderColor: textColor === '#ffffff' ? '#fff' : textColor,
                        color: backgroundColor.includes('52c41a') ? '#52c41a' : '#000',
                        fontWeight: '600',
                        opacity: (!userAnswer || (Array.isArray(userAnswer) && userAnswer.length === 0)) ? 0.5 : 1
                      }}
                    >
                      {questionNumber === questions.length ? "Finish Quiz" : "Next"}
                    </Button>
                  </div>
                </div>
              </Col>

              {/* Question Image Column - Right side (like PDF) */}
              {currentQuestion?.image && (
                <Col xs={24} md={10} order={{ xs: 1, md: 2 }}>
                  <div style={{ textAlign: 'center' }}>
                    <Image
                      src={currentQuestion.image}
                      alt="Question"
                      width="100%"
                      height={400}
                      style={{ 
                        objectFit: 'cover',
                        borderRadius: '16px',
                        maxWidth: '100%'
                      }}
                      preview={true}
                    />
                  </div>
                </Col>
              )}
            </Row>
          </div>
        </div>
      </div>
    );
  }

  // Default fallback
  return (
    <div style={pageStyle}>
      <div style={containerStyle}>
        <Text style={{ color: textColor }}>Invalid step</Text>
      </div>
    </div>
  );
};

export default QuizPlayer;